import React, { useState } from 'react';
import {
  MatterStatusPieChart,
  BillingTrendChart,
  ClientActivityChart,
  MatterVolumeBarChart,
  AttorneyUtilizationChart,
  AttorneyRevenueChart,
  WeeklyProductivityChart,
  ClientMatterDistributionChart,
  ChartWidget
} from '../charts/ChartPrototypes';// Hot-swappable dashboard for rapid prototyping
const DashboardPrototype: React.FC = () => {
  // Toggle states for experimenting with different layouts
  const [layout, setLayout] = useState<'grid' | 'rows' | 'columns'>('grid');
  const [showMetrics, setShowMetrics] = useState(true);
  const [viewMode, setViewMode] = useState<'general' | 'attorney'>('general');

  // Mock metrics data
  const metrics = [
    { label: 'Active Matters', value: '42', change: '+8%', trend: 'up' },
    { label: 'Total Billing', value: '$345K', change: '+12%', trend: 'up' },
    { label: 'New Clients', value: '7', change: '-2%', trend: 'down' },
    { label: 'Completion Rate', value: '89%', change: '+5%', trend: 'up' }
  ];

  const attorneyMetrics = [
    { label: 'Billable Hours (MTD)', value: '89', change: '+12%', trend: 'up' },
    { label: 'Utilization Rate', value: '94%', change: '+8%', trend: 'up' },
    { label: 'Revenue (MTD)', value: '$22.5K', change: '+15%', trend: 'up' },
    { label: 'Matter Count', value: '12', change: '+2', trend: 'up' }
  ];

  const getLayoutClasses = () => {
    if (viewMode === 'attorney') {
      // Special layout for attorney view with full-width weekly chart
      switch (layout) {
        case 'rows':
          return 'grid grid-cols-1 gap-6';
        case 'columns':
          return 'grid grid-cols-1 lg:grid-cols-2 gap-6';
        default:
          return 'grid grid-cols-1 lg:grid-cols-2 gap-6';
      }
    }
    // Standard layout for general view
    switch (layout) {
      case 'rows':
        return 'grid grid-cols-1 gap-6';
      case 'columns':
        return 'grid grid-cols-2 gap-6';
      default:
        return 'grid grid-cols-1 lg:grid-cols-2 gap-6';
    }
  };

  return (
    <div className="space-y-6">
      {/* Hot-swap Controls */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <h2 className="text-lg font-semibold text-white mb-4">🔥 Hot-Swap Controls</h2>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-gray-300">View Mode:</label>
            <select 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value as any)}
              className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            >
              <option value="general">General Dashboard</option>
              <option value="attorney">Attorney Productivity</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-300">Layout:</label>
            <select 
              value={layout} 
              onChange={(e) => setLayout(e.target.value as any)}
              className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            >
              <option value="grid">Grid</option>
              <option value="rows">Rows</option>
              <option value="columns">Columns</option>
            </select>
          </div>
          <label className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={showMetrics} 
              onChange={(e) => setShowMetrics(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-300">Show Metrics</span>
          </label>
        </div>
      </div>

      {/* Metrics Cards */}
      {showMetrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(viewMode === 'attorney' ? attorneyMetrics : metrics).map((metric, index) => (
            <div key={index} className="bg-gray-800 rounded-lg border border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{metric.label}</p>
                  <p className="text-white text-2xl font-bold">{metric.value}</p>
                </div>
                <div className={`text-sm font-medium ${
                  metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {metric.change}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts Grid */}
      <div className={getLayoutClasses()}>
        {viewMode === 'general' ? (
          <>
            <ChartWidget title="Matter Status Distribution">
              <MatterStatusPieChart />
            </ChartWidget>

            <ChartWidget title="Billing Trends (6 Months)">
              <BillingTrendChart />
            </ChartWidget>

            <ChartWidget title="Client Activity">
              <ClientActivityChart />
            </ChartWidget>

            <ChartWidget title="Matter Volume">
              <MatterVolumeBarChart />
            </ChartWidget>
          </>
        ) : (
          <>
            {/* Top row - two charts */}
            <div className="col-span-1">
              <ChartWidget title="Attorney Utilization vs Targets">
                <AttorneyUtilizationChart />
              </ChartWidget>
            </div>

            <div className="col-span-1">
              <ChartWidget title="Attorney Revenue Trend">
                <AttorneyRevenueChart />
              </ChartWidget>
            </div>

            {/* Full width chart for weekly productivity */}
            <div className="col-span-full">
              <ChartWidget title="Weekly Productivity (Trailing 12 Weeks)">
                <WeeklyProductivityChart />
              </ChartWidget>
            </div>

            {/* Bottom chart */}
            <div className="col-span-1">
              <ChartWidget title="Client Matter Distribution">
                <ClientMatterDistributionChart />
              </ChartWidget>
            </div>
          </>
        )}
      </div>

      {/* Prototyping Notes */}
      <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
        <h3 className="text-blue-300 font-semibold mb-2">🎨 Rapid Prototyping Notes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-blue-200 font-medium mb-2">Hot-Swap Features:</h4>
            <ul className="text-blue-200 text-sm space-y-1">
              <li>• Switch between General & Attorney views</li>
              <li>• Test different layout arrangements</li>
              <li>• Toggle metrics visibility</li>
              <li>• Charts auto-resize responsively</li>
            </ul>
          </div>
          <div>
            <h4 className="text-blue-200 font-medium mb-2">Attorney Dashboard Features:</h4>
            <ul className="text-blue-200 text-sm space-y-1">
              <li>• Trailing 12-week productivity view</li>
              <li>• Matter-type breakdown in stacked bars</li>
              <li>• Full-width weekly chart for detail</li>
              <li>• Revenue & utilization tracking</li>
            </ul>
          </div>
        </div>
        <p className="text-blue-300 text-sm mt-3 font-medium">
          💡 Edit ChartPrototypes.tsx to instantly modify chart designs and data
        </p>
      </div>
    </div>
  );
};

export default DashboardPrototype;
